import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, tap } from 'rxjs';
import { md5 } from 'js-md5';
import { EnvironmentType } from '../interfaces';
import { LanguageStateService } from '../services/language-state.service';

// CD-2983: browser i18n (dashboard/calculator) is served per-env from Cloudflare
// R2 (edge-cached + purge-on-publish), replacing the nginx `expires -1` box that
// died with the origin. Each env hits its OWN bucket host so the bucket CORS
// policy scopes to that env's app origins (the shared nginx `lang.coindepo.io`
// returned no Access-Control-Allow-Origin). Prod keeps the bare host (its DNS is
// cut over to R2 at release); dev/stage use the R2 custom-domain subdomains.
const LANG_EDGE_HOSTS: Record<EnvironmentType, string> = {
  dev: 'https://dev-lang.coindepo.io',
  stage: 'https://stage-lang.coindepo.io',
  prod: 'https://lang.coindepo.com',
};

export class CustomTranslateHttpLoader extends TranslateHttpLoader {
  private httpClient: HttpClient;

  constructor(
    http: HttpClient,
    private type: 'dashboard' | 'calculator' | 'governance',
    private crowdlId: string,
    private lanuage: LanguageStateService,
    prefix: string = './assets/i18n/',
    suffix: string = '.json',
    private env: EnvironmentType = 'prod',
  ) {
    super(http, prefix, suffix);
    this.httpClient = http;
  }

  override getTranslation(lang: string | undefined): Observable<any> {
    if (lang === 'only-key') {
      return of({});
    }
    if (lang === 'undefined' || lang === undefined) {
      lang = 'en';
    }
    if (localStorage.getItem('translate-source') === 'crowdl') {
      const timestamp = Date.now().toString();
      const hash = this.getHash(timestamp);
  
      return this.httpClient.get(
        `https://api.crowdl.io/${this.crowdlId}/translations`,
        {
          params: {
            api_key: 'tkLEF5rdayPUxR7PkkVfzqWgWE97PUA8',
            timestamp,
            locale: lang,
            include_missing: false,
            format: 'hierarchical-json',
            hash,
          },
        }
      ).pipe(
        tap(() => this.lanuage.setLoaded()),
        catchError(err => {
          console.error('Crowdl error', err);
          return of({});
        })
      );
    }
    if (localStorage.getItem('translate-source') === 'local') {
      return super.getTranslation(lang).pipe(
        catchError(() => of({})),
        tap(() => this.lanuage.setLoaded())
      );
    }
    // Defensive default only — a valid EnvironmentType always hits the map.
    // Fall back to PROD (never dev): a mis-set `env` must not route prod users
    // to the dev bucket; prod also serves the full canonical translation set.
    const host = LANG_EDGE_HOSTS[this.env] ?? LANG_EDGE_HOSTS.prod;
    return this.httpClient.get(`${host}/${this.type}/${lang}${this.suffix}`).pipe(
      catchError(() => of({})),
      tap(() => this.lanuage.setLoaded())
    );
  }

  getHash(timestamp: string): string {
    const secret = 'FWkbZnLo1oIH3KEw0CnmQ22hvn56o3vs';
    return md5(timestamp + secret);
  }
}
